/*
 * CAUTION: The values below are relied upon by the P0 CLI. Any changes made here may need to be reflected in and should
 * be tested in the P0 CLI.
 */
import type {
  AccessRequestOf,
  ArgsOf,
  RequestOf,
} from "@p0security/shared/types/access/index";
import { Access } from "@p0security/shared/types/access/index";

const awsResource = {
  type: "object",
  label: "Resource data",
  optional: true,
  properties: {
    account: {
      type: "generated",
      label: "Account",
    },
    arn: {
      type: "generated",
      label: "Resource ARN",
      description: "The ARN of the resource",
    },
    name: {
      type: "generated",
      label: "Resource name",
      description: "The name of the resource",
    },
    object: {
      type: "generated",
      label: "Object",
      optional: true,
    },
    policies: {
      type: "generated",
      label: "Policies",
      multivalued: true,
    },
    extends: {
      type: "generated",
      label: "Extends",
      optional: true,
    },
    extendsName: {
      type: "generated",
      label: "Extend policies",
      optional: true,
    },
    service: {
      type: "generated",
      label: "Service",
    },
    accountId: {
      type: "generated",
      label: "Account ID",
    },
    idcArn: {
      type: "generated",
      label: "IDC ARN",
      optional: true,
    },
    idcId: {
      type: "generated",
      label: "IDC ID",
      optional: true,
    },
    idcRegion: {
      type: "generated",
      label: "IDC region",
      optional: true,
    },
    region: {
      type: "generated",
      label: "Region",
    },
    userName: {
      type: "generated",
      label: "User name",
    },
    resourceTag: {
      type: "object",
      label: "Resource tag",
      optional: true,
      properties: {
        key: {
          type: "generated",
          label: "Key",
        },
        value: {
          type: "generated",
          label: "Value",
        },
      },
    },
  },
} as const;

const awsGeneratedField = {
  type: "object",
  label: "AWS resource request",
  optional: true,
  properties: {
    arn: {
      type: "string",
      label: "Resource ARN",
      description: "The ARN of the resource",
    },
    name: {
      type: "string",
      label: "Role name",
    },
    policyArn: {
      type: "string",
      label: "Policy ARN",
    },
    policyName: {
      type: "string",
      label: "Policy name",
    },
    policyDocument: {
      type: "string",
      label: "Policy document",
    },
  },
} as const;

const integrationType = {
  type: "generated",
  label: "Installation type",
  choices: ["cloud-sql", "rds"],
} as const;

const userCreatedByP0 = {
  type: "string",
} as const;

const instance = {
  type: "static",
  label: "PostgreSQL instance",
  description: "The PostgreSQL instance in which access should be granted",
  isPreselect: true,
} as const;

const instanceName = {
  type: "generated",
  label: "PostgreSQL instance name",
  description: "The user friendly name of the PostgreSQL instance",
} as const;

const databaseName = {
  type: "generated",
  label: "PostgreSQL database name",
  description: "The user friendly name of the PostgreSQL database",
} as const;

const parent = {
  type: "generated",
  label: "Project ID",
  description: "The ID of the project in which the PostgreSQL instance resides",
} as const;

const region = {
  type: "generated",
  label: "Region",
  description: "The region in which the PostgreSQL instance resides",
} as const;

const grants = {
  type: "object",
  label: "Grants",
  description: "The SQL grants required to run the query",
} as const;

const customRoleInfo = {
  type: "object",
  label: "Custom role",
  description: "Information about the custom role to be created",
  properties: {
    roleName: {
      label: "Role name",
      type: "generated",
    },
    accessInfo: {
      type: "object",
      label: "Access info",
      multivalued: true,
      properties: {
        schemaName: {
          type: "generated",
          label: "Schema name",
          optional: true,
        },
        tableName: {
          type: "generated",
          label: "Table name",
          optional: true,
        },
        permissionType: {
          type: "generated",
          label: "Permission type",
        },
      },
    },
  },
} as const;

const common = {
  instanceName,
  databaseName,
  integrationType,
  parent,
  region,
  resource: awsResource,
};

export const PostgresLegacyAccesses = {
  role: Access({
    description: "postgreSQL role grant",
    label: "Role",
    maturity: "ga",
    delegation: {},
    generated: {
      userCreatedByP0,
      resource: awsGeneratedField,
    },
    parameters: {
      instance,
      role: {
        type: "dynamic",
        label: "Role name",
        description: "Role name",
      },
      ...common,
    },
    render: { locator: "instance", policy: "role" } as const,
  }),
  sql: Access({
    description: "Infer required permissions by parsing a SQL script",
    label: "SQL query",
    maturity: "ga",
    delegation: {},
    generated: {
      userCreatedByP0,
      resource: awsGeneratedField,
    },
    parameters: {
      instance,
      text: {
        type: "input",
        label: "Query",
        multiline: true,
        description:
          "The body of the SQL script - may contain multiple SQL statements",
      },
      ...common,
      grants,
      customRoleInfo,
    },
    render: { locator: "instance" } as const,
  }),
};

export type PostgresLegacyAccesses = typeof PostgresLegacyAccesses;
export type PostgresLegacyAccess =
  PostgresLegacyAccesses[keyof PostgresLegacyAccesses];

export type PostgresLegacyRoleAccess = PostgresLegacyAccesses["role"];
export type PostgresLegacySqlAccess = PostgresLegacyAccesses["sql"];

export type PostgresLegacyRequest = RequestOf<PostgresLegacyAccess>;
export type PostgresLegacyRoleRequest = RequestOf<PostgresLegacyRoleAccess>;
export type PostgresLegacySqlRequest = RequestOf<PostgresLegacySqlAccess>;

export type PostgresLegacyAccessRequest =
  AccessRequestOf<PostgresLegacyAccesses>;

export type PostgresLegacyArgs = ArgsOf<PostgresLegacyAccess>;

export type PostgresLegacyCustomRoleInfo =
  PostgresLegacySqlRequest["permission"]["customRoleInfo"];
export type PostgresLegacyAccessInfo =
  PostgresLegacyCustomRoleInfo["accessInfo"][number];
